import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { analyticsService } from '@/services/analytics'
import { useAuthStore } from '@/stores/auth'
import type {
  DashboardResponse,
  DateRangeParams,
  InsightsApiResult,
  ProductsResponse,
  TokenUsageResponse,
  TrendsResponse,
} from '@/types/analytics'

/** UUID v4 format validation regex */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Validates that a string is a valid UUID format.
 * @param id - The string to validate
 * @returns true if valid UUID, false otherwise
 */
function isValidUUID(id: string | undefined): id is string {
  return !!id && UUID_REGEX.test(id)
}

/**
 * Query key factory for analytics data
 * Ensures consistent cache key structure across all analytics queries
 */
export const analyticsKeys = {
  all: ['analytics'] as const,
  dashboard: (configId: string, dateRange: DateRangeParams, orgId?: string) =>
    [...analyticsKeys.all, 'dashboard', configId, dateRange, orgId] as const,
  trends: (configId: string, dateRange: DateRangeParams, orgId?: string) =>
    [...analyticsKeys.all, 'trends', configId, dateRange, orgId] as const,
  products: (configId: string, dateRange: DateRangeParams, orgId?: string) =>
    [...analyticsKeys.all, 'products', configId, dateRange, orgId] as const,
  insights: (configId: string, dateRange: DateRangeParams, orgId?: string) =>
    [...analyticsKeys.all, 'insights', configId, dateRange, orgId] as const,
  orgTokens: (orgId: string | undefined, dateRange: DateRangeParams) =>
    [...analyticsKeys.all, 'orgTokens', orgId, dateRange] as const,
  askAiStarters: (configId: string) =>
    [...analyticsKeys.all, 'ask_ai_starters', configId] as const,
}

interface UseAnalyticsOptions {
  enabled?: boolean
  staleTime?: number
}

const DEFAULT_STALE_TIME = 5 * 60 * 1000 // 5 minutes

/**
 * Hook for fetching dashboard analytics metrics
 * Returns totals, time series, and browser breakdown
 */
export function useAnalyticsDashboard(
  configId: string | undefined,
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const organizationId = useAuthStore((state) => state.organization?.id)
  const { enabled = true, staleTime = DEFAULT_STALE_TIME } = options

  // Validate organizationId format
  const isValidOrgId = isValidUUID(organizationId)

  return useQuery<DashboardResponse>({
    queryKey: analyticsKeys.dashboard(
      configId || '',
      dateRange,
      isValidOrgId ? organizationId : undefined
    ),
    queryFn: () => {
      if (!configId) {
        throw new Error('Configuration ID is required')
      }

      if (!isValidOrgId) {
        throw new Error('Valid organization context is required')
      }

      return analyticsService.getDashboard(configId, dateRange)
    },
    enabled: enabled && !!configId && isValidOrgId,
    staleTime,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for fetching trend analytics metrics
 * Returns daily conversation and session trends with summaries
 */
export function useAnalyticsTrends(
  configId: string | undefined,
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const organizationId = useAuthStore((state) => state.organization?.id)
  const { enabled = true, staleTime = DEFAULT_STALE_TIME } = options

  // Validate organizationId format
  const isValidOrgId = isValidUUID(organizationId)

  return useQuery<TrendsResponse>({
    queryKey: analyticsKeys.trends(
      configId || '',
      dateRange,
      isValidOrgId ? organizationId : undefined
    ),
    queryFn: () => {
      if (!configId) {
        throw new Error('Configuration ID is required')
      }
      if (!isValidOrgId) {
        throw new Error('Valid organization context is required')
      }
      return analyticsService.getTrends(configId, dateRange)
    },
    enabled: enabled && !!configId && isValidOrgId,
    staleTime,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for fetching product performance metrics
 * Returns top products by views and clicks with CTR
 */
export function useAnalyticsProducts(
  configId: string | undefined,
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const organizationId = useAuthStore((state) => state.organization?.id)
  const { enabled = true, staleTime = DEFAULT_STALE_TIME } = options

  // Validate organizationId format
  const isValidOrgId = isValidUUID(organizationId)

  return useQuery<ProductsResponse>({
    queryKey: analyticsKeys.products(
      configId || '',
      dateRange,
      isValidOrgId ? organizationId : undefined
    ),
    queryFn: () => {
      if (!configId) {
        throw new Error('Configuration ID is required')
      }
      if (!isValidOrgId) {
        throw new Error('Valid organization context is required')
      }
      return analyticsService.getProducts(configId, dateRange)
    },
    enabled: enabled && !!configId && isValidOrgId,
    staleTime,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for fetching conversation insights.
 * The API responds synchronously with aggregator output — no polling needed.
 */
export function useAnalyticsInsights(
  configId: string | undefined,
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const organizationId = useAuthStore((state) => state.organization?.id)
  const { enabled = true, staleTime = DEFAULT_STALE_TIME } = options

  const isValidOrgId = isValidUUID(organizationId)

  const query = useQuery<InsightsApiResult>({
    queryKey: analyticsKeys.insights(
      configId || '',
      dateRange,
      isValidOrgId ? organizationId : undefined
    ),
    queryFn: () => {
      if (!configId) {
        throw new Error('Configuration ID is required')
      }
      if (!isValidOrgId) {
        throw new Error('Valid organization context is required')
      }
      return analyticsService.getInsights(configId, dateRange)
    },
    enabled: enabled && !!configId && isValidOrgId,
    staleTime,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const refresh = useCallback(() => {
    query.refetch()
  }, [query])

  return {
    ...query,
    insights: query.data?.data,
    refresh,
  }
}

/**
 * Combined hook for fetching all analytics data at once
 * Useful for the main analytics page where all data is needed
 */
export function useAnalyticsData(
  configId: string | undefined,
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const dashboard = useAnalyticsDashboard(configId, dateRange, options)
  const trends = useAnalyticsTrends(configId, dateRange, options)
  const products = useAnalyticsProducts(configId, dateRange, options)

  return {
    dashboard: dashboard.data,
    trends: trends.data,
    products: products.data,
    isLoading: dashboard.isLoading || trends.isLoading || products.isLoading,
    isFetching:
      dashboard.isFetching || trends.isFetching || products.isFetching,
    error: dashboard.error || trends.error || products.error,
    refetch: () => {
      dashboard.refetch()
      trends.refetch()
      products.refetch()
    },
  }
}

/**
 * Hook for fetching token usage totals + time series for the caller's organization.
 */
export function useOrganizationTokenUsage(
  dateRange: DateRangeParams,
  options: UseAnalyticsOptions = {}
) {
  const organizationId = useAuthStore((state) => state.organization?.id)
  const { enabled = true, staleTime = DEFAULT_STALE_TIME } = options

  return useQuery<TokenUsageResponse>({
    queryKey: analyticsKeys.orgTokens(organizationId, dateRange),
    queryFn: () => analyticsService.getOrganizationTokenUsage(dateRange),
    enabled: enabled && !!organizationId,
    staleTime,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })
}
